import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../services/firebase/config';
import { Sun, Moon, ChevronDown, Settings, HelpCircle, Bell, LogOut, User, Briefcase, Shield } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import PortfolioSelector from '../portfolio/PortfolioSelector';

function Header() {
  const [user] = useAuthState(auth);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const navigate = useNavigate();
  const profileRef = React.useRef(null);
  const currencyRef = React.useRef(null);

  const { selectedCurrency, setSelectedCurrency, getFlag } = useCurrency();
  const SUPPORTED_CURRENCIES = [
    { code: 'USD', name: 'Amerikan Doları' },
    { code: 'EUR', name: 'Euro' },
    { code: 'GBP', name: 'İngiliz Sterlini' },
    { code: 'JPY', name: 'Japon Yeni' },
    { code: 'CHF', name: 'İsviçre Frangı' },
    { code: 'TRY', name: 'Türk Lirası' }
  ];

  useEffect(() => {
    const darkMode = localStorage.getItem('darkMode') === 'true';
    setIsDarkMode(darkMode);
    document.documentElement.classList.toggle('dark', darkMode);
    document.body.classList.toggle('dark:bg-[#0A0E23]', darkMode);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (currencyRef.current && !currencyRef.current.contains(event.target)) {
        setIsCurrencyOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCurrencySelect = (currencyCode) => {
    setSelectedCurrency(currencyCode);
    setIsCurrencyOpen(false);
  };

  return (
    <header className="bg-white dark:bg-[#151934] border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Sol Taraf */}
          <div className="flex items-center gap-8">
            <Link to="/" className="text-xl font-bold text-gray-900 dark:text-white">
              Smart Portföy
            </Link>
            
            {user && (
              <div className="flex items-center gap-6">
                <PortfolioSelector />
                <div className="hidden md:flex items-center gap-6 border-l border-gray-200 dark:border-gray-700 pl-6 h-8">
                  <Link 
                    to="/dashboard" 
                    className="text-sm font-medium text-gray-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-white transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link 
                    to="/portfolio-ibkr" 
                    className="text-sm font-medium text-gray-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-white transition-colors"
                  >
                    Portföy (IBKR)
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Sağ Taraf */}
          <div className="flex items-center gap-4">
            {/* Para Birimi Seçici */}
            <div className="relative" ref={currencyRef}>
              <button
                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-gray-100 dark:hover:bg-[#1f2544]"
              >
                <span className="flex items-center gap-1">
                  <img src={getFlag(selectedCurrency)} alt={selectedCurrency} className="h-5 w-5" />
                  <span className="hidden md:inline text-sm text-gray-700 dark:text-gray-300">{selectedCurrency}</span>
                </span>
                <ChevronDown size={16} className="text-gray-500" />
              </button>

              {isCurrencyOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#151934] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  {SUPPORTED_CURRENCIES.map(({ code, name }) => (
                    <button
                      key={code}
                      onClick={() => handleCurrencySelect(code)}
                      className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300"
                    >
                      <img src={getFlag(code)} alt={code} className="h-5 w-5 mr-2" />
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">{code}</span>
                        <span className="text-xs text-gray-500 hidden md:inline">{name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ...existing dark mode toggle and profile menu... */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2"
              >
                <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                  {user?.email?.[0].toUpperCase() || 'U'}
                </div>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#151934] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[999]">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="font-medium text-gray-900 dark:text-white">{user?.displayName || user?.email}</p>
                    <p className="text-sm text-gray-500">Investor</p>
                  </div>
                  
                  <div className="py-2">
                    <button onClick={() => navigate('/dashboard')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <User className="w-4 h-4 mr-3" />
                      <span>Dashboard</span>
                    </button>
                    <button onClick={() => navigate('/settings')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <Settings className="w-4 h-4 mr-3" />
                      <span>Portföy Ayarlari</span>
                    </button>
                    <button onClick={() => navigate('/rebalance-settings')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <Settings className="w-4 h-4 mr-3" />
                      <span>Rebalance Ayarları</span>
                    </button>

                    <button onClick={() => navigate('/sinyaller/sapan-sinyalleri')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                                          <Settings className="w-4 h-4 mr-3" />
                                          <span>Sapan Sinyalleri</span>
                                        </button>


                    </div>

                   
                                         
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <button onClick={() => navigate('/account/settings')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <User className="w-4 h-4 mr-3" />
                      <span>Hesabım</span>
                    </button>

                    {user?.email === 'zaferkekik@gmail.com' && (
                      <button onClick={() => navigate('/admin')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                        <Shield className="w-4 h-4 mr-3" />
                        <span>Admin Panel</span>
                      </button>
                    )}
                    
                    <button onClick={() => navigate('/#')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <Bell className="w-4 h-4 mr-3" />
                      <span>Bildirimler</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <button onClick={() => navigate('/importData')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <Briefcase className="w-4 h-4 mr-3" />
                      <span>Verileri içe Aktar</span>
                    </button>
                  </div>

                  
                  <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <button onClick={() => navigate('/portfolio/positions')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <Briefcase className="w-4 h-4 mr-3" />
                      <span>Posizyonlar</span>
                    </button>
                  </div>


                  <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <button onClick={() => navigate('/support')} className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300">
                      <HelpCircle className="w-4 h-4 mr-3" />
                      <span>Destek</span>
                    </button>

                    <button 
                      onClick={() => {
                        setIsDarkMode(!isDarkMode);
                        localStorage.setItem('darkMode', String(!isDarkMode));
                        document.documentElement.classList.toggle('dark');
                        document.body.classList.toggle('dark:bg-[#0A0E23]');
                      }} 
                      className="flex items-center w-full px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#1f2544] text-gray-700 dark:text-gray-300"
                    >
                      {isDarkMode ? <Sun className="w-4 h-4 mr-3" /> : <Moon className="w-4 h-4 mr-3" />}
                      <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 py-2">
                    <button
                      onClick={() => {
                        auth.signOut();
                        navigate('/login');
                      }}
                      className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-[#1f2544]"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;